import React from 'react'
import styled from 'styled-components'
import { Flex } from 'reflexbox/styled-components'
import PropTypes from 'prop-types'

import media from 'media'

const StyledContainer = styled(Flex).attrs({
  alignItems: 'center',
  flexDirection: 'column',
  width: '100%',
})``

const getCellAttrs = ({ theme, width, isSecondChild }) => {
  return {
    width: width ? (Array.isArray(width) ? width : [(1, 1, width)]) : null,
    alignItems: 'center',
    justifyContent: 'center',
  }
}

export const Cell = styled(Flex).attrs(getCellAttrs)`
  ${({ width }) =>
    !width &&
    `
    flex: 1;
  `}
`

const getRowAttrs = ({
  mobileStyling,
  isLastRow,
  shouldFlipMobileItems,
  shouldFlipItems,
}) => ({
  flexDirection: [
    shouldFlipMobileItems ? 'column-reverse' : 'column',
    shouldFlipMobileItems ? 'column-reverse' : 'column',
    shouldFlipItems ? 'row-reverse' : 'row',
  ],
  mb: isLastRow ? 0 : [50, 50, 100],
})

export const Row = styled(Flex)
  .attrs(getRowAttrs)
  .withConfig({
    shouldForwardProp: (prop) =>
      [
        'childrenHaveEqualWidth',
        'numChildren',
        'isLastRow',
        'shouldFlipMobileItems',
        'shouldFlipItems',
      ].indexOf(prop) === -1,
  })`
  width: 100%;

  ${({ childrenHaveEqualWidth, numChildren }) =>
    childrenHaveEqualWidth &&
    media.desktop`
        & > * {
            width: ${100 / numChildren}%;
        }
    `}
`

/**
 * confusing, yes.
 * but for a 2 column layout, we have multiple options for how to render
 * 3 main questions:
 * - what is the starting item?
 * - what is the orientation? straight or swapped? (swapped is alternating layout)
 * - what is the mobile orientation? straight or swapped?
 */
const Container = ({
  rows,
  mobileStyling,
  mobileOrientation,
  orientation,
  mobileStartsWith,
  startsWith,
  ...others
}) => {
  const renderRow = (row, index) => {
    // FOR NOW: constrained to rows of only 2 cells, but should be the bases for any number
    if (row?.length !== 2) {
      throw new Error('Rows with more or less than 2 cells are not supported.')
    }

    const isMobileSwapped = mobileOrientation === 'swapped'
    const isSwapped = orientation === 'swapped'

    const childrenHaveEqualWidth = !row.some((cell) => cell.width)
    const shouldFlipMobileItems = isMobileSwapped
      ? (mobileStartsWith === 'text' && index % 2 === 1) ||
        (mobileStartsWith === 'asset' && index % 2 === 0)
      : mobileStartsWith === 'asset'

    const shouldFlipItems = isSwapped
      ? (startsWith === 'text' && index % 2 === 1) ||
        (startsWith === 'asset' && index % 2 === 0)
      : startsWith === 'asset'

    return (
      <Row
        childrenHaveEqualWidth={childrenHaveEqualWidth}
        numChildren={row.length}
        shouldFlipMobileItems={shouldFlipMobileItems}
        shouldFlipItems={shouldFlipItems}
        mobileStyling={mobileStyling}
        isLastRow={index === rows.length - 1}
        className="split-row"
      >
        {row.map(({ key, renderChildren, width, className }, index) => (
          <Cell key={key} width={width} className={className}>
            {renderChildren()}
          </Cell>
        ))}
      </Row>
    )
  }

  return <StyledContainer {...others}>{rows.map(renderRow)}</StyledContainer>
}

Container.defaultProps = {
  mobileStyling: 'vertical',
  orientation: 'straight',
  mobileOrientation: 'straight',
}

Container.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        width: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
          PropTypes.array,
        ]),
        renderChildren: PropTypes.func.isRequired,
      })
    )
  ),
  mobileOrientation: PropTypes.oneOf(['straight', 'swapped']),
  orientation: PropTypes.oneOf(['straight', 'swapped']),
  mobileStartsWith: PropTypes.oneOf(['text', 'asset']),
  startsWith: PropTypes.oneOf(['text', 'asset']),
  // TODO: this
  mobileStyling: PropTypes.string, // will control how mobile looks. make the cells go completely vertical? or push one of them off the screen a bit?
}

export default Container
